/**
 * Replace `window.sessionStorage` with an in-memory Storage when the browser
 * denies access to it (site data blocked, sandboxed/third-party iframes, some
 * in-app browsers). Remix's lazy route discovery and scroll restoration read
 * `sessionStorage` directly, so a throwing getter broke client navigations
 * with "Failed to read the 'sessionStorage' property from 'Window'".
 *
 * Only `sessionStorage` is shimmed: an empty in-memory `localStorage` would
 * trip the cache-version check in root.tsx into a reload loop.
 */
export function installSessionStorageFallback(): void {
  if (typeof window === "undefined") return;

  try {
    window.sessionStorage.getItem("__storage_probe__");

    return;
  } catch {
    // Access denied: fall through and install the in-memory replacement.
  }

  try {
    Object.defineProperty(window, "sessionStorage", {
      value: createMemoryStorage(),
      configurable: true,
    });
  } catch {
    // The property cannot be redefined in this browser; nothing else to do.
  }
}

function createMemoryStorage(): Storage {
  const items = new Map<string, string>();

  return {
    get length() {
      return items.size;
    },
    clear: () => items.clear(),
    getItem: (key: string) => items.get(String(key)) ?? null,
    key: (index: number) => Array.from(items.keys())[index] ?? null,
    removeItem: (key: string) => {
      items.delete(String(key));
    },
    setItem: (key: string, value: string) => {
      items.set(String(key), String(value));
    },
  };
}
